<template>
  <div class="workorder-list">
    <!-- 页面标题和搜索栏 -->
    <div class="list-header">
      <div class="header-left">
        <div class="page-title">
          <el-button
              type="text"
              @click="goToHome"
              class="home-btn"
          >
            <el-icon>
              <HomeFilled/>
            </el-icon>
            首页
          </el-button>
          <div>
            <h2>工单管理</h2>
            <p>创建、查看和处理工单</p>
          </div>
        </div>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="navigateToCreateWorkOrder" v-permission="['workorder.create']">
          <el-icon>
            <Plus/>
          </el-icon>
          新建工单
        </el-button>
      </div>
    </div>

    <!-- 筛选条件 -->
    <el-card class="filter-card">
      <div class="filter-header">
        <span class="filter-title">筛选条件</span>
        <span class="filter-expand-btn" :class="{ 'is-expanded': filterExpanded }" @click="filterExpanded = !filterExpanded">
          {{ filterExpanded ? '收起' : '展开' }}
          <el-icon><ArrowDown /></el-icon>
        </span>
      </div>

      <!-- 常用筛选（始终显示） -->
      <div class="filter-grid">
        <div class="filter-item">
          <span class="filter-label">工单号</span>
          <div class="filter-control">
            <el-input
                v-model="filterForm.order_number"
                placeholder="模糊匹配工单号"
                clearable
                @keyup.enter="handleFilterChange"
            />
          </div>
        </div>

        <div class="filter-item">
          <span class="filter-label">部署环境</span>
          <div class="filter-control">
            <el-select
                v-model="filterForm.deployment_environment_id"
                placeholder="请选择部署环境"
                clearable
                :loading="loadingEnvironments"
                @change="handleFilterChange"
                @visible-change="onEnvDropdownVisible"
            >
              <template v-if="deploymentEnvironments.length === 0">
                <el-option disabled value="">
                  <span style="color: #909399;">暂无部署环境数据</span>
                </el-option>
              </template>
              <el-option
                  v-for="env in deploymentEnvironments"
                  :key="env.id"
                  :label="getEnvDisplayName(env)"
                  :value="env.id"
              />
            </el-select>
          </div>
        </div>

        <div class="filter-item">
          <span class="filter-label">处理人</span>
          <div class="filter-control">
            <el-select
                v-model="filterForm.handler_id"
                placeholder="请选择处理人"
                clearable
                filterable
                remote
                :remote-method="loadHandlerOptions"
                :loading="loadingHandlers"
                @change="handleFilterChange"
                @focus="loadHandlerOptions('')"
            >
              <el-option
                  v-for="handler in handlerOptions"
                  :key="handler.id"
                  :label="getUserDisplayName(handler)"
                  :value="handler.id"
              />
            </el-select>
          </div>
        </div>

        <div class="filter-item">
          <span class="filter-label">状态</span>
          <div class="filter-control">
            <el-select v-model="filterForm.status" placeholder="全部状态" clearable @change="handleFilterChange">
              <el-option label="待处理" value="open"/>
              <el-option label="处理中" value="in_progress"/>
              <el-option label="待确认" value="pending"/>
              <el-option label="已解决" value="resolved"/>
              <el-option label="已关闭" value="closed"/>
              <el-option label="已删除" value="deleted"/>
            </el-select>
          </div>
        </div>

        <div class="filter-item">
          <span class="filter-label">优先级</span>
          <div class="filter-control">
            <el-select v-model="filterForm.priority" placeholder="全部优先级" clearable @change="handleFilterChange">
              <el-option label="低" value="low"/>
              <el-option label="中" value="medium"/>
              <el-option label="高" value="high"/>
              <el-option label="紧急" value="urgent"/>
            </el-select>
          </div>
        </div>

        <div class="filter-item">
          <span class="filter-label">创建时间</span>
          <div class="filter-control">
            <el-date-picker
                v-model="filterForm.dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                @change="handleFilterChange"
            />
          </div>
        </div>
      </div>

      <!-- 更多筛选（可展开/收起） -->
      <transition name="filter-collapse">
        <div v-show="filterExpanded" class="filter-grid filter-advanced">
          <div class="filter-item">
            <span class="filter-label">环境</span>
            <div class="filter-control">
              <el-select v-model="filterForm.environment" placeholder="全部环境" clearable @change="handleFilterChange">
                <el-option label="测试环境" value="test"/>
                <el-option label="预发环境" value="staging"/>
                <el-option label="生产环境" value="production"/>
              </el-select>
            </div>
          </div>

          <div class="filter-item">
            <span class="filter-label">租户版本</span>
            <div class="filter-control">
              <el-select v-model="filterForm.tenant_version" placeholder="全部版本" clearable @change="handleFilterChange">
                <el-option label="1.0" value="1.0"/>
                <el-option label="2.0" value="2.0"/>
                <el-option label="3.0" value="3.0"/>
              </el-select>
            </div>
          </div>

          <div class="filter-item">
            <span class="filter-label">手机类型</span>
            <div class="filter-control">
              <el-select v-model="filterForm.work_phone_type" placeholder="全部类型" clearable @change="handleFilterChange">
                <el-option label="营销" value="营销"/>
                <el-option label="政企" value="政企"/>
              </el-select>
            </div>
          </div>

          <div class="filter-item">
            <span class="filter-label">租户ID</span>
            <div class="filter-control">
              <el-input
                  v-model="filterForm.tenant_id"
                  placeholder="模糊匹配租户ID"
                  clearable
                  @keyup.enter="handleFilterChange"
              />
            </div>
          </div>

          <div class="filter-item">
            <span class="filter-label">手机版本</span>
            <div class="filter-control">
              <el-input
                  v-model="filterForm.work_phone_version"
                  placeholder="模糊匹配手机版本"
                  clearable
                  @keyup.enter="handleFilterChange"
              />
            </div>
          </div>
        </div>
      </transition>

      <div class="filter-actions">
        <el-button type="primary" @click="handleFilterChange">搜索</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>
    </el-card>

    <!-- 工单表格 -->
    <el-card class="list-card">
      <template #header>
        <div class="card-header">
          <span>工单列表</span>
          <div class="header-actions">
            <el-button type="text" @click="exportData">导出</el-button>
            <el-button type="text" @click="refreshList">刷新</el-button>
          </div>
        </div>
      </template>

      <el-table
          :data="workorders"
          v-loading="loading"
          @row-click="handleRowClick"
          style="width: 100%"
      >
        <el-table-column prop="order_number" label="工单号" width="140" fixed>
          <template #default="{ row }">
            <el-tag type="info" size="small">{{ row.order_number }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="title" label="标题" min-width="200">
          <template #default="{ row }">
            <div class="title-cell">
              <span class="title-text">{{ row.title }}</span>
              <span v-if="row.priority === 'urgent'" class="urgent-tag">紧急</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="tenant_name" label="租户" width="120"/>

        <el-table-column prop="environment" label="环境" width="80">
          <template #default="{ row }">
            <el-tag :type="getEnvType(row.environment)" size="small">
              {{ getEnvText(row.environment) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="priority" label="优先级" width="80">
          <template #default="{ row }">
            <el-tag :type="getPriorityType(row.priority)" size="small">
              {{ getPriorityText(row.priority) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="current_handler.username" label="处理人" width="100"/>

        <el-table-column prop="creator.username" label="创建人" width="100"/>

        <el-table-column prop="created_at" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button type="text" size="small" @click.stop="viewDetail(row)">
              查看
            </el-button>
            <el-dropdown @command="(command) => handleCommand(command, row)" trigger="click">
              <el-button type="text" size="small">
                <el-icon>
                  <More/>
                </el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="edit" v-if="hasEditPermission(row)">编辑</el-dropdown-item>
                  <el-dropdown-item command="assign" v-if="hasAssignPermission(row)">分配</el-dropdown-item>
                  <el-dropdown-item command="delete" v-if="hasDeletePermission(row)" divided>
                    删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.per_page"
            :total="pagination.total"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 创建工单对话框 -->
    <el-dialog
        v-model="createDialogVisible"
        title="新建工单"
        width="600px"
        @closed="resetCreateForm"
    >
      <el-form :model="createForm" :rules="createRules" ref="createFormRef" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="createForm.title" placeholder="请输入工单标题"/>
        </el-form-item>

        <el-form-item label="问题描述" prop="description">
          <el-input
              v-model="createForm.description"
              type="textarea"
              :rows="4"
              placeholder="请详细描述遇到的问题..."
          />
        </el-form-item>

        <el-row>
          <el-col :span="12">
            <el-form-item label="环境" prop="environment">
              <el-select v-model="createForm.environment" placeholder="请选择环境">
                <el-option label="测试环境" value="test"/>
                <el-option label="预发环境" value="staging"/>
                <el-option label="生产环境" value="production"/>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="优先级" prop="priority">
              <el-select v-model="createForm.priority" placeholder="请选择优先级">
                <el-option label="低" value="low"/>
                <el-option label="中" value="medium"/>
                <el-option label="高" value="high"/>
                <el-option label="紧急" value="urgent"/>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="12">
            <el-form-item label="租户版本" prop="tenant_version">
              <el-input v-model="createForm.tenant_version" placeholder="如：v2.5.1"/>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="租户ID" prop="tenant_id">
              <el-input v-model="createForm.tenant_id" placeholder="请输入租户ID"/>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="手机信息">
          <el-row :gutter="10">
            <el-col :span="8">
              <el-input v-model="createForm.work_phone_type" placeholder="手机类型"/>
            </el-col>
            <el-col :span="8">
              <el-input v-model="createForm.work_phone_version" placeholder="手机版本"/>
            </el-col>
            <el-col :span="8">
              <el-input v-model="createForm.work_phone_model" placeholder="手机型号"/>
            </el-col>
          </el-row>
        </el-form-item>

        <el-form-item label="附件">
          <el-upload
              action="#"
              :before-upload="beforeUpload"
              :on-remove="handleRemove"
              :file-list="fileList"
              multiple
              :limit="5"
          >
            <el-button type="primary">
              <el-icon>
                <Upload/>
              </el-icon>
              选择文件
            </el-button>
            <template #tip>
              <div class="el-upload__tip">
                支持图片、文档、压缩包等格式，最大100MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="createDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitCreateForm" :loading="creating">
            创建
          </el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog
        v-model="assignDialogVisible"
        title="分配工单"
        width="500px"
        @closed="resetAssignForm"
    >
      <el-form :model="assignForm" :rules="assignRules" ref="assignFormRef" label-width="100px">
        <el-form-item label="工单号">
          <el-input v-model="assignForm.orderNumber" disabled/>
        </el-form-item>

        <el-form-item label="新处理人" prop="newHandlerId">
          <el-select
              v-model="assignForm.newHandlerId"
              placeholder="请选择处理人"
              filterable
              remote
              :remote-method="loadAssignUsers"
              :loading="loadingUsers"
              style="width: 100%;"
              @focus="loadAssignUsers('')"
          >
            <!-- 优先显示当前处理人 -->
            <el-option
                v-if="currentAssignOrder && currentAssignOrder.current_handler"
                :key="currentAssignOrder.current_handler.id"
                :label="getUserDisplayName(currentAssignOrder.current_handler)"
                :value="currentAssignOrder.current_handler.id"
                class="current-handler-option"
            />

            <!-- 显示其他用户，排除当前处理人 -->
            <el-option
                v-for="user in userList.filter(u => !currentAssignOrder || !currentAssignOrder.current_handler || u.id !== currentAssignOrder.current_handler.id)"
                :key="user.id"
                :label="getUserDisplayName(user)"
                :value="user.id"
            />

            <!-- 如果没有用户数据 -->
            <template v-if="userList.length === 0">
              <el-option disabled value="">
                <span style="color: #909399;">暂无用户数据，请输入关键词搜索</span>
              </el-option>
            </template>
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
      <span class="dialog-footer">
        <el-button @click="assignDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAssignForm" :loading="assigning">
          确认分配
        </el-button>
      </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import {onMounted, reactive, ref} from 'vue'
import {useRouter} from 'vue-router'
import {ElMessage, ElMessageBox} from 'element-plus'
import {checkPermission} from '@/api/auth'
import {HomeFilled, More, Plus, Upload, ArrowDown} from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import {createWorkOrder as apiCreateWorkOrder, getAssignUsers, getWorkOrders, updateWorkOrder} from '@/api/workorder'
import {getDeploymentEnvironments} from "@/api/deployment";
import {getPermissions} from "@/api/userGroup";

const router = useRouter()

// 响应式数据
const loading = ref(false)
const creating = ref(false)
const createDialogVisible = ref(false)
const workorders = ref([])
const fileList = ref([])
const hasEnvironmentPermission = ref(true)
const loadingEnvironments = ref(false)
const deploymentEnvironments = ref([])
const filterExpanded = ref(false)

// 筛选下拉选项数据
const loadingHandlers = ref(false)
const handlerOptions = ref([])

// 分配相关的响应式数据
const assignDialogVisible = ref(false)
const assigning = ref(false)
const loadingUsers = ref(false)
const userList = ref([])
const currentAssignOrder = ref(null)
// authStore.user

// 分页数据
const pagination = reactive({
  page: 1,
  per_page: 20,
  total: 0,
  pages: 0
})

const assignForm = reactive({
  orderNumber: '',
  newHandlerId: '',
})

const assignFormRef = ref(null)

// 分配表单验证规则
const assignRules = {
  newHandlerId: [
    {required: true, message: '请选择处理人', trigger: 'change'}
  ]
}

// // 权限相关
// const hasEditPermission = ref(false)
// const hasAssignPermission = ref(false)
// const hasDeletePermission = ref(false)

// 获取用户显示名称
const getUserDisplayName = (user) => {
  if (!user) return ''
  if (user.username && user.full_name) {
    return `${user.username} - ${user.full_name}`
  }
  return user.username || user.full_name || `用户${user.id}`
}

// 获取用户列表
const loadAssignUsers = async (keyword = '') => {
  try {
    loadingUsers.value = true
    const response = await getAssignUsers({
      username: keyword,
      limit: 10 // 每次只显示前10条
    })

    // 根据API响应结构调整
    if (response && response.data) {
      const apiUsers = response.data.users || []

      // 重要：确保当前处理人在用户列表中
      if (currentAssignOrder.value && currentAssignOrder.value.current_handler) {
        const currentHandler = currentAssignOrder.value.current_handler
        // 检查当前处理人是否已经在API返回的用户列表中
        const handlerExists = apiUsers.some(user => user.id === currentHandler.id)

        if (!handlerExists) {
          // 如果当前处理人不在API返回的列表中，将其添加到列表开头
          apiUsers.unshift(currentHandler)
          console.log('将当前处理人添加到分配用户列表:', currentHandler.username)
        }
      }

      userList.value = apiUsers
      console.log('加载分配用户列表成功:', userList.value.length, '个用户')
    } else {
      userList.value = response.users || []
    }
  } catch (error) {
    console.error('加载分配用户列表失败:', error)
    // 如果API调用失败，至少确保当前处理人在列表中
    if (currentAssignOrder.value && currentAssignOrder.value.current_handler) {
      userList.value = [currentAssignOrder.value.current_handler]
      console.log('使用当前处理人作为用户列表:', userList.value[0].username)
    } else {
      userList.value = []
    }
  } finally {
    loadingUsers.value = false
  }
}


// 检查权限
const checkOperationPermission = async (operation, row) => {
  try {
    // 调用权限API检查
    const response = await getPermissions()

    // 根据实际权限数据结构调整
    const permissions = response.data?.permissions || response.permissions || []

    // 查找对应操作的权限
    const operationPermission = permissions.find(p =>
        p.code === `workorder.${operation}` ||
        p.name.includes(operation)
    )

    // 也可以根据用户角色和工单状态判断
    const userRole = localStorage.getItem('user_role') || 'user'
    const isAdmin = userRole === 'admin'
    const isCreator = row.creator_id === parseInt(localStorage.getItem('user_id'))
    const isCurrentHandler = row.current_handler_id === parseInt(localStorage.getItem('user_id'))

    // 根据操作类型判断权限
    switch (operation) {
      case 'edit':
        // 管理员、创建者或当前处理人可以编辑
        return isAdmin || isCreator || isCurrentHandler

      case 'assign':
        // 管理员或当前处理人可以分配
        return isAdmin || isCurrentHandler

      case 'delete':
        // 只有管理员或创建者可以删除
        return isAdmin || isCreator

      default:
        return false
    }

  } catch (error) {
    console.error('权限检查失败:', error)
    return false
  }
}

// 处理下拉菜单命令
const handleCommand = async (command, row) => {
  console.log('执行命令:', command, '工单:', row.order_number)

  // 直接从工单的 permissions 对象中获取权限
  let hasPermission = false;

  switch (command) {
    case 'edit':
      hasPermission = hasEditPermission(row)
      break
    case 'assign':
      hasPermission = hasAssignPermission(row)
      break
    case 'delete':
      hasPermission = hasDeletePermission(row)
      break
    default:
      console.warn('未知命令:', command)
  }

  if (!hasPermission) {
    ElMessage.warning('您没有执行此操作的权限')
    return
  }

  switch (command) {
    case 'edit':
      await handleEdit(row)
      break
    case 'assign':
      await handleAssign(row)
      break
    case 'delete':
      await handleDelete(row)
      break
    default:
      console.warn('未知命令:', command)
  }
}

// 编辑工单 - 跳转到详情页
const handleEdit = async (row) => {
  try {
    // 跳转到工单详情页（假设详情页可以编辑）
    await router.push(`/workorders/${row.id}`)
  } catch (error) {
    console.error('跳转失败:', error)
    ElMessage.error('无法打开工单详情')
  }
}

// 分配工单 - 打开分配弹窗
// 在 handleAssign 方法中调用新方法
const handleAssign = async (row) => {
  try {
    currentAssignOrder.value = row
    assignForm.orderNumber = row.order_number
    assignForm.newHandlerId = row.current_handler?.id || ''

    // 初始加载用户列表
    await loadAssignUsers('')

    // 确保当前处理人在选择框中显示
    if (row.current_handler && row.current_handler.id) {
      // 如果用户列表中没有当前处理人，添加进去
      const handlerExists = userList.value.some(user => user.id === row.current_handler.id)
      if (!handlerExists) {
        userList.value.unshift(row.current_handler)
        console.log('手动添加当前处理人到用户列表:', row.current_handler.username)
      }
    }

    assignDialogVisible.value = true
  } catch (error) {
    console.error('准备分配表单失败:', error)
    ElMessage.error('准备分配表单失败')
  }
}


// 重置分配表单
const resetAssignForm = () => {
  assignForm.newHandlerId = ''
  currentAssignOrder.value = null
  if (assignFormRef.value) {
    assignFormRef.value.resetFields()
  }
}

// 提交分配表单
const submitAssignForm = async () => {
  if (!assignFormRef.value) return

  try {
    // 验证表单
    await assignFormRef.value.validate()

    // 检查是否选择了新处理人
    if (!assignForm.newHandlerId) {
      ElMessage.warning('请选择新处理人')
      return
    }

    assigning.value = true

    // 准备请求数据
    const assignData = {
      current_handler_id: assignForm.newHandlerId
    }

    // 调用API更新工单（只更新处理人）
    const response = await updateWorkOrder(currentAssignOrder.value.id, assignData)

    // 关闭弹窗
    assignDialogVisible.value = false

    // 显示成功消息
    ElMessage.success({
      message: '工单分配成功',
      duration: 3000
    })

    // 刷新工单列表
    await loadWorkOrders()

  } catch (error) {
    console.error('分配工单失败:', error)
    ElMessage.error(error.response?.data?.message || '分配失败')
  } finally {
    assigning.value = false
  }
}

// 删除工单
const handleDelete = async (row) => {
  try {
    // 二次确认弹窗
    await ElMessageBox.confirm(
        `<div style="margin-bottom: 10px;">确定要删除工单 <strong>${row.order_number}</strong> 吗？</div>
       <div style="color: #f56c6c; font-size: 12px;">删除后将无法恢复，请谨慎操作。</div>`,
        '确认删除',
        {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true,
          customClass: 'delete-confirm-dialog'
        }
    )

    // 调用删除API - 注意：这里需要导入或定义 deleteWorkOrder 函数
    // await deleteWorkOrder(row.id)

    // 显示成功消息
    ElMessage.success({
      message: `工单 ${row.order_number} 已删除`,
      duration: 3000
    })

    // 刷新工单列表
    await loadWorkOrders()

  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除工单失败:', error)
      ElMessage.error(error.response?.data?.message || '删除失败')
    }
  }
}

const viewDetail = (order) => {
  router.push(`/workorders/${order.id}`)
}

// 修改权限检查函数 - 现在直接从工单的 permissions 对象中获取
const hasEditPermission = (row) => {
  if (!row.permissions) return false
  return row.permissions.can_edit || false
}

const hasAssignPermission = (row) => {
  if (!row.permissions) return false
  return row.permissions.can_assign || false
}

const hasDeletePermission = (row) => {
  if (!row.permissions) return false
  return row.permissions.can_delete || false
}

// 筛选表单（字段与后端 GET /work/workorders/ 列表接口保持一致）
const filterForm = reactive({
  order_number: '',                 // 模糊匹配
  deployment_environment_id: null,  // id 精确匹配（部署环境接口下拉）
  handler_id: null,                 // id 精确匹配（可分配用户接口远程搜索下拉）
  status: '',
  priority: '',
  environment: '',                  // 精确匹配（列表接口返回的环境选项）
  tenant_version: '',               // 精确匹配（列表接口返回的版本选项）
  work_phone_type: '',              // 精确匹配（列表接口返回的手机类型选项）
  tenant_id: '',                    // 模糊匹配
  work_phone_version: '',           // 模糊匹配
  dateRange: []
})

const goToHome = () => {
  router.push('/dashboard')
}

// 创建表单
const createForm = reactive({
  title: '',
  description: '',
  environment: '',
  priority: 'medium',
  tenant_version: '',
  tenant_id: '',
  tenant_name: '',
  work_phone_type: '',
  work_phone_version: '',
  work_phone_model: ''
})

const createFormRef = ref(null)

const loadDeploymentEnvironments = async () => {
  try {
    console.log('开始加载部署环境列表...')
    const response = await getDeploymentEnvironments()
    // console.log('部署环境接口响应:', response)

    // 根据您的信息，部署环境对象路径是 response.data.environments
    if (response && response.data && response.data.environments) {
      deploymentEnvironments.value = response.data.environments
      // console.log('成功加载部署环境列表:', deploymentEnvironments.value)
    } else if (response && response.environments) {
      // 如果直接在 response.environments
      deploymentEnvironments.value = response.environments
    } else if (response && response.data && response.data.deployment_environments) {
      // 如果路径是 response.data.deployment_environments
      deploymentEnvironments.value = response.data.deployment_environments
    } else {
      // console.warn('部署环境数据格式不符合预期:', response)
      deploymentEnvironments.value = []
      ElMessage.warning('部署环境数据格式异常')
    }

    // 如果没有数据，尝试使用单独的部署环境API
    if (deploymentEnvironments.value.length === 0) {
      console.log('尝试使用单独的部署环境API...')
      try {
        // 导入部署环境API
        const {getDeploymentEnvironments} = await import('@/api/deployment')
        const envResponse = await getDeploymentEnvironments({per_page: 100})

        if (envResponse && envResponse.data) {
          if (envResponse.data.environments) {
            deploymentEnvironments.value = envResponse.data.environments
          } else if (envResponse.data.data) {
            deploymentEnvironments.value = envResponse.data.data
          }
        }
        console.log('从部署环境API加载的数据:', deploymentEnvironments.value)
      } catch (apiError) {
        console.error('从部署环境API加载失败:', apiError)
      }
    }

  } catch (error) {
    console.error('加载部署环境失败:', error)
    console.error('错误详情:', error.response)

    // 如果 getFilterOptions 失败，尝试直接获取部署环境
    try {
      console.log('尝试备用方法加载部署环境...')
      // 尝试直接调用部署环境API
      const {getDeploymentEnvironments} = await import('@/api/deployment')
      const backupResponse = await getDeploymentEnvironments({per_page: 100})

      if (backupResponse && backupResponse.data) {
        if (backupResponse.data.environments) {
          deploymentEnvironments.value = backupResponse.data.environments
        } else if (Array.isArray(backupResponse.data)) {
          deploymentEnvironments.value = backupResponse.data
        } else if (backupResponse.data.data) {
          deploymentEnvironments.value = backupResponse.data.data
        }
      }

      console.log('备用方法加载的部署环境:', deploymentEnvironments.value)
    } catch (backupError) {
      console.error('备用方法也失败:', backupError)
      ElMessage.error('加载部署环境失败，请检查网络连接')
    }
  }
}

const getEnvDisplayName = (env) => {
  if (!env) return ''
  // 如果环境有名称和代码，显示为：名称 (代码)
  if (env.name && env.code) {
    return `${env.name} (${env.code})`
  }
  // 否则显示名称或代码
  return env.name || env.code || '未知环境'
}

// 处理筛选变化
const handleFilterChange = () => {
  // 重置到第一页
  pagination.page = 1
  loadWorkOrders()
}

// 当下拉框显示时触发
const onEnvDropdownVisible = (visible) => {
  if (visible && deploymentEnvironments.value.length === 0) {
    loadDeploymentEnvironments()
  }
}

// 远程搜索处理人选项（调用可分配用户接口，按用户名模糊匹配）
const loadHandlerOptions = async (keyword = '') => {
  try {
    loadingHandlers.value = true
    const response = await getAssignUsers({
      username: keyword || '',
      limit: 20
    })

    const apiUsers = response?.data?.users || []

    // 确保已选中的处理人保留在选项中（远程搜索后选项列表会刷新）
    if (filterForm.handler_id) {
      const selected = handlerOptions.value.find(u => u.id === filterForm.handler_id)
      if (selected && !apiUsers.some(u => u.id === selected.id)) {
        apiUsers.unshift(selected)
      }
    }

    handlerOptions.value = apiUsers
  } catch (error) {
    console.error('加载处理人选项失败:', error)
    handlerOptions.value = []
  } finally {
    loadingHandlers.value = false
  }
}

const navigateToCreateWorkOrder = async () => {
  try {
    // 检查权限
    // console.log(111)
    const permissionResponse = await checkPermission(
        'workorder.create'
    )

    if (permissionResponse.data.has_permission) {
      await router.push('/workorders/create')
    } else {
      ElMessage.warning('您没有创建工单的权限')
    }
  } catch (error) {
    console.error('权限检查失败:', error)
    ElMessage.error('权限检查失败')
  }
}

// 验证规则
const createRules = {
  title: [
    {required: true, message: '请输入标题', trigger: 'blur'},
    {min: 5, max: 100, message: '标题长度在5到100个字符', trigger: 'blur'}
  ],
  description: [
    {required: true, message: '请输入问题描述', trigger: 'blur'},
    {min: 10, message: '请详细描述问题，至少10个字符', trigger: 'blur'}
  ],
  environment: [
    {required: true, message: '请选择环境', trigger: 'change'}
  ],
  priority: [
    {required: true, message: '请选择优先级', trigger: 'change'}
  ],
  tenant_id: [
    {required: true, message: '请输入租户ID', trigger: 'blur'}
  ]
}

// 方法
const loadWorkOrders = async () => {
  try {
    loading.value = true

    // 构建查询参数（字段与后端 GET /work/workorders/ 保持一致，空值不传）
    const params = {
      page: pagination.page,
      per_page: pagination.per_page,
      sort_by: 'created_at',
      sort_order: 'desc'
    }

    // 模糊匹配字段（字符串输入框）
    if (filterForm.order_number) params.order_number = filterForm.order_number
    if (filterForm.tenant_id) params.tenant_id = filterForm.tenant_id
    if (filterForm.work_phone_version) params.work_phone_version = filterForm.work_phone_version

    // id 精确匹配字段（下拉选项）
    if (filterForm.deployment_environment_id) {
      params.deployment_environment_id = filterForm.deployment_environment_id
    }
    if (filterForm.handler_id) params.handler_id = filterForm.handler_id

    // 精确匹配枚举字段（下拉选项）
    if (filterForm.status) params.status = filterForm.status
    if (filterForm.priority) params.priority = filterForm.priority
    if (filterForm.environment) params.environment = filterForm.environment
    if (filterForm.tenant_version) params.tenant_version = filterForm.tenant_version
    if (filterForm.work_phone_type) params.work_phone_type = filterForm.work_phone_type

    // 添加日期范围
    if (filterForm.dateRange && filterForm.dateRange.length === 2) {
      params.created_start = filterForm.dateRange[0] + ' 00:00:00'
      params.created_end = filterForm.dateRange[1] + ' 23:59:59'
    }

    // 调用真实API
    const response = await getWorkOrders(params)

    // 根据实际API响应结构调整
    if (response.data) {
      workorders.value = response.data.workorders || []
      pagination.total = response.data.total || 0
      pagination.pages = response.data.pages || Math.ceil(pagination.total / pagination.per_page)

      // 用后端返回的过滤选项更新下拉选项
      const filters = response.data.filters || {}
      if (Array.isArray(filters.available_handlers) && filters.available_handlers.length > 0) {
        handlerOptions.value = filters.available_handlers
      }
      // 部署环境：接口返回的完整数据（含 code）优先，filters 数据兜底
      if (deploymentEnvironments.value.length === 0 && Array.isArray(filters.deployment_environments)) {
        deploymentEnvironments.value = filters.deployment_environments
      }
    } else {
      workorders.value = response.workorders || []
      pagination.total = response.total || 0
      pagination.pages = response.pages || Math.ceil(pagination.total / pagination.per_page)
    }

  } catch (error) {
    console.error('加载工单失败:', error)
    ElMessage.error(error.response?.data?.message || error.response?.data?.detail || '加载工单失败')
  } finally {
    loading.value = false
  }
}

// 修改这里：重命名为 handleCreateWorkOrder
const handleCreateWorkOrder = () => {
  // 创建工单前检查权限
  checkCreatePermission()
}

const checkCreatePermission = async () => {
  try {
    // 默认检查生产环境权限
    const permissionResponse = await checkPermission(
        'workorder.create'
    )

    if (permissionResponse.data.has_permission) {
      createDialogVisible.value = true
    } else {
      ElMessage.warning('您没有创建工单的权限')
    }
  } catch (error) {
    console.error('权限检查失败:', error)
    ElMessage.error('权限检查失败')
  }
}

const submitCreateForm = async () => {
  if (!createFormRef.value) return

  try {
    await createFormRef.value.validate()
    creating.value = true

    // 准备表单数据
    const formData = new FormData()
    formData.append('title', createForm.title)
    formData.append('description', createForm.description)
    formData.append('environment', createForm.environment)
    formData.append('priority', createForm.priority)
    formData.append('tenant_version', createForm.tenant_version)
    formData.append('tenant_id', createForm.tenant_id)

    if (createForm.tenant_name) {
      formData.append('tenant_name', createForm.tenant_name)
    }
    if (createForm.work_phone_type) {
      formData.append('work_phone_type', createForm.work_phone_type)
    }
    if (createForm.work_phone_version) {
      formData.append('work_phone_version', createForm.work_phone_version)
    }
    if (createForm.work_phone_model) {
      formData.append('work_phone_model', createForm.work_phone_model)
    }

    // 添加上传的文件
    fileList.value.forEach(file => {
      if (file.raw) {
        formData.append('attachments', file.raw)
      }
    })

    // 调用重命名后的API函数
    const response = await apiCreateWorkOrder(formData)

    createDialogVisible.value = false
    ElMessage.success(response.message || '工单创建成功')
    await loadWorkOrders()

  } catch (error) {
    console.error('创建工单失败:', error)
    ElMessage.error(error.response?.data?.message || '创建失败')
  } finally {
    creating.value = false
  }
}


const deleteWorkOrder = async (row) => {
  try {
    await ElMessageBox.confirm(
        `确定要删除工单 "${row.order_number}" 吗？删除后不可恢复。`,
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
    )

    // 调用真实API
    await deleteWorkOrder(row.id)
    ElMessage.success('删除成功')
    await loadWorkOrders()

  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除工单失败:', error)
      ElMessage.error(error.response?.data?.message || '删除失败')
    }
  }
}

const resetFilters = () => {
  Object.keys(filterForm).forEach(key => {
    if (Array.isArray(filterForm[key])) {
      filterForm[key] = []
    } else if (key === 'deployment_environment_id' || key === 'handler_id') {
      filterForm[key] = null
    } else {
      filterForm[key] = ''
    }
  })
  pagination.page = 1
  loadWorkOrders()
}

const handleSizeChange = (size) => {
  pagination.per_page = size
  loadWorkOrders()
}

const handleCurrentChange = (page) => {
  pagination.page = page
  loadWorkOrders()
}

const refreshList = () => {
  loadWorkOrders()
  ElMessage.success('已刷新')
}

const exportData = () => {
  ElMessage.info('导出功能开发中')
}

const beforeUpload = (file) => {
  const isLt100M = file.size / 1024 / 1024 < 100
  if (!isLt100M) {
    ElMessage.error('文件大小不能超过100MB')
    return false
  }
  return false // 阻止自动上传
}

const handleRemove = (file, fileList) => {
  // 处理文件移除
}

const getEnvType = (env) => {
  const types = {
    test: 'info',
    staging: 'warning',
    production: 'danger'
  }
  return types[env] || ''
}

const getEnvText = (env) => {
  const texts = {
    test: '测试环境',
    staging: '预发环境',
    production: '生产环境'
  }
  return texts[env] || env
}

const getPriorityType = (priority) => {
  const types = {
    low: 'info',
    medium: '',
    high: 'warning',
    urgent: 'danger'
  }
  return types[priority] || ''
}

const getPriorityText = (priority) => {
  const texts = {
    low: '低',
    medium: '中',
    high: '高',
    urgent: '紧急'
  }
  return texts[priority] || priority
}

const getStatusType = (status) => {
  const types = {
    open: 'info',
    in_progress: 'warning',
    pending: '',
    resolved: 'success',
    closed: 'danger',
    deleted: 'danger'
  }
  return types[status] || ''
}

const getStatusText = (status) => {
  const texts = {
    open: '待处理',
    in_progress: '处理中',
    pending: '待确认',
    resolved: '已解决',
    closed: '已关闭',
    deleted: '已删除'
  }
  return texts[status] || status
}

const formatDate = (date) => {
  return dayjs(date).format('YYYY-MM-DD HH:mm')
}

// const userPermissions = ref([])

// // 加载用户权限
// const loadUserPermissions = async () => {
//   try {
//     const response = await getPermissions({
//       per_page: 100
//     })
//
//     // 根据实际API响应结构调整
//     const permissionsData = response.data?.permissions || response.permissions || []
//
//     // 提取权限码数组
//     userPermissions.value = permissionsData
//       .filter(perm => perm.is_active) // 只保留激活的权限
//       .map(perm => perm.code)
//
//     console.log('加载用户权限成功:', userPermissions.value)
//   } catch (error) {
//     console.error('加载用户权限失败:', error)
//     ElMessage.error('加载用户权限失败')
//     // 如果权限接口失败，使用默认权限（只有管理员）
//     const currentUserRole = localStorage.getItem('user_role') || 'user'
//     if (currentUserRole === 'admin') {
//       userPermissions.value = [
//         'workorder.view',
//         'workorder.create',
//         'workorder.edit',
//         'workorder.assign',
//         'workorder.delete'
//       ]
//     } else {
//       userPermissions.value = ['workorder.view']
//     }
//   }
// }

// // 通用权限检查函数
// const checkPermission = (permissionCode) => {
//   return userPermissions.value.includes(permissionCode)
// }

// 生命周期
onMounted(() => {
  // console.log('List.vue mounted, 开始加载数据...')
  loadWorkOrders()
  loadDeploymentEnvironments()
})
</script>

<style scoped lang="scss">

.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.workorder-list {
  padding: 20px;

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;

    .header-left {
      h2 {
        margin: 0 0 8px 0;
        font-size: 24px;
        color: var(--text-primary);
      }

      p {
        margin: 0;
        color: var(--text-secondary);
        font-size: 14px;
      }
    }
  }

  .filter-card {
    margin-bottom: 20px;

    .filter-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;

      .filter-title {
        font-size: 15px;
        font-weight: 600;
        color: var(--text-primary);
      }
    }

    .filter-advanced {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px dashed var(--border-lighter);
    }
  }

  .list-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-actions {
        display: flex;
        align-items: center;
      }
    }

    .title-cell {
      display: flex;
      align-items: center;
      gap: 8px;

      .title-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .urgent-tag {
        background: rgba(245, 108, 108, 0.1);
        color: var(--app-danger);
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
      }
    }

    .pagination {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
  }
}

:deep(.el-table__row) {
  cursor: pointer;
}

.delete-confirm-dialog {
  .el-message-box__title {
    color: var(--app-danger);
    font-weight: bold;
  }

  .el-message-box__headerbtn {
    color: var(--app-danger);
  }

  .el-button--primary {
    background-color: var(--app-danger);
    border-color: var(--app-danger);

    &:hover {
      background-color: #f78989;
      border-color: #f78989;
    }
  }
}

/* 分配弹窗样式 */
.assign-dialog {
  .el-dialog__body {
    padding: 20px;
  }

  .el-form-item {
    margin-bottom: 20px;
  }

  .el-input.is-disabled .el-input__inner {
    background-color: var(--bg-hover);
    color: var(--text-secondary);
  }
}

/* 操作按钮样式优化 */
:deep(.el-table__row .el-dropdown) {
  .el-button {
    padding: 0;
    margin-left: 5px;
  }

  .el-icon {
    font-size: 16px;
  }
}
</style>